import { useEffect, useState } from 'react'
import api from '@/services/api'

/**
 * Vendors enter the exact name on their Google Business Profile.
 * Things to Do operator listings match this string. Saving it does not
 * turn on Google distribution; that stays an ops decision.
 */

const NAME_MAX = 100

interface GoogleBusinessProfileSectionProps {
  vendorId?: string
}

function isProfileLink(value: string): boolean {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return true
  if (/\s/.test(trimmed)) return false
  return /(?:google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps|business\.google\.com)/i.test(trimmed)
}

export default function GoogleBusinessProfileSection({ vendorId }: GoogleBusinessProfileSectionProps) {
  const [name, setName] = useState('')
  const [savedName, setSavedName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!vendorId) return
    api.vendors
      .get(vendorId)
      .then((res) => {
        const data = res.data?.data ?? res.data
        const current = typeof data?.googleBusinessProfileName === 'string'
          ? data.googleBusinessProfileName
          : ''
        setName(current)
        setSavedName(current || null)
      })
      .catch(() => {
        setError('Could not load your Google Business Profile.')
      })
      .finally(() => setLoading(false))
  }, [vendorId])

  const save = async () => {
    if (!vendorId) return
    setError(null)
    setSuccess(null)

    const trimmed = name.trim()
    if (trimmed.length > NAME_MAX) {
      setError(`Google Business Profile name cannot exceed ${NAME_MAX} characters.`)
      return
    }
    if (isProfileLink(trimmed)) {
      setError('Enter the business name shown on Google, not a link.')
      return
    }

    setSaving(true)
    try {
      const res = await api.vendors.update(vendorId, {
        googleBusinessProfileName: trimmed,
      })
      const data = res.data?.data ?? res.data
      const stored = typeof data?.googleBusinessProfileName === 'string'
        ? data.googleBusinessProfileName
        : ''
      setName(stored)
      setSavedName(stored || null)
      setSuccess(stored ? 'Google Business Profile saved.' : 'Google Business Profile removed.')
    } catch (err: any) {
      const details = err?.response?.data?.error?.details
      const detailMessage = Array.isArray(details) ? details[0]?.message : null
      setError(
        detailMessage
          || err?.response?.data?.error?.message
          || 'Could not save your Google Business Profile.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (!vendorId) return null

  const searchQuery = (name.trim() || savedName || '').trim()
  const mapsSearchUrl = searchQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`
    : 'https://www.google.com/maps'

  return (
    <div className="card">
      <h3 style={{ marginBottom: '0.5rem' }}>Google Business Profile</h3>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Google lists your experiences under this name. Enter it exactly as it appears on your
        Google Business Profile, including spelling and punctuation. Saving the name here does
        not publish your experiences to Google on its own.
      </p>

      {loading ? (
        <p style={{ color: 'var(--color-gray-500)' }}>Loading...</p>
      ) : (
        <>
          <label htmlFor="google-business-profile-name" style={labelStyle}>
            Profile name
          </label>
          <input
            id="google-business-profile-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
              setSuccess(null)
            }}
            placeholder="The name at the top of your Google listing"
            style={inputStyle}
            autoComplete="organization"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.4rem' }}>
            <p style={helperStyle}>
              Copy the name from{' '}
              <a href="https://business.google.com/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Google Business Profile
              </a>
              {' '}or{' '}
              <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Google Maps
              </a>
              . Leave the field empty and save to remove it.
            </p>
            <span style={{ ...helperStyle, flexShrink: 0, color: name.trim().length > NAME_MAX ? 'var(--color-error)' : 'var(--color-gray-500)' }}>
              {name.trim().length}/{NAME_MAX}
            </span>
          </div>

          {savedName && (
            <p style={{ ...helperStyle, marginTop: '0.75rem' }}>
              Currently saved as <strong>{savedName}</strong>.
            </p>
          )}

          {error && (
            <p style={{ color: 'var(--color-error)', marginTop: '1rem', fontSize: '0.9rem' }}>
              {error}
            </p>
          )}

          {success && (
            <p style={{ color: 'var(--color-success)', marginTop: '1rem', fontSize: '0.9rem' }}>
              {success}
            </p>
          )}

          <button
            type="button"
            onClick={save}
            disabled={saving || name.trim() === (savedName || '')}
            style={{
              marginTop: '1.5rem',
              padding: '0.5rem 1.5rem',
              background: saving || name.trim() === (savedName || '') ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving || name.trim() === (savedName || '') ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            {saving ? 'Saving...' : savedName ? 'Update profile name' : 'Save profile name'}
          </button>
        </>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.9rem',
}

const helperStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.8rem',
  color: 'var(--color-gray-500)',
  lineHeight: 1.45,
}

const linkStyle: React.CSSProperties = {
  color: 'var(--color-tixxly-navy)',
}
